import React, { useState, useEffect } from 'react';
import carService, { Car, CreateCarRequest, UpdateCarRequest, PaginatedResponse, CarStats } from '../services/carService';
import './CarManagement.css';

interface CarManagementProps {
  onClose?: () => void;
}

const CarManagement: React.FC<CarManagementProps> = ({ onClose }) => {
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCar, setEditingCar] = useState<Car | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [carStats, setCarStats] = useState<CarStats | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
  const [selectedCars, setSelectedCars] = useState<number[]>([]);

  // Формы
  const [createForm, setCreateForm] = useState<CreateCarRequest>({
    name: '',
    model: '',
    number: 0,
    imei: '',
    airport: '',
    isActive: true
  });

  const [editForm, setEditForm] = useState<UpdateCarRequest>({
    name: '',
    model: '',
    number: 0,
    imei: '',
    airport: '',
    isActive: true
  });

  const airports = ['ASTANA', 'ALMATY', 'ALA', 'NQZ', 'GUW', 'URA', 'AKX', 'SCO', 'KGF'];
  const models = ['КАМАЗ-53215', 'ГАЗ-2705', 'УРАЛ-4320', 'ЗИЛ-131', 'МАЗ-5335'];
  const sortOptions = [
    { value: 'id', label: 'ID' },
    { value: 'name', label: 'Название' },
    { value: 'model', label: 'Модель' },
    { value: 'number', label: 'Номер' },
    { value: 'imei', label: 'IMEI' },
    { value: 'airport', label: 'Аэропорт' },
    { value: 'createdAt', label: 'Дата создания' }
  ];

  useEffect(() => {
    fetchCars();
    fetchCarStats();
  }, [currentPage, pageSize, sortBy, sortDir]);

  const fetchCars = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<Car> = await carService.getCars(currentPage, pageSize, sortBy, sortDir);
      setCars(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Error fetching cars:', err);
      setError('Ошибка при загрузке машин');
    } finally {
      setLoading(false);
    }
  };

  const fetchCarStats = async () => {
    try {
      const stats = await carService.getCarStats();
      setCarStats(stats);
    } catch (err) {
      console.error('Error fetching car stats:', err);
    }
  };

  const handleCreateCar = async () => {
    try {
      if (!createForm.name || !createForm.model || !createForm.imei || createForm.number <= 0) {
        alert('Пожалуйста, заполните все обязательные поля');
        return;
      }

      if (createForm.imei.length !== 15) {
        alert('IMEI должен содержать 15 цифр');
        return;
      }

      await carService.createCar(createForm);
      setIsCreateModalOpen(false);
      setCreateForm({
        name: '',
        model: '',
        number: 0,
        imei: '',
        airport: '',
        isActive: true
      });
      fetchCars();
      fetchCarStats();
      alert('Машина успешно создана!');
    } catch (err) {
      console.error('Error creating car:', err);
      alert('Ошибка при создании машины');
    }
  };

  const handleUpdateCar = async () => {
    try {
      if (!editingCar) return;

      const updateData: UpdateCarRequest = {};
      if (editForm.name) updateData.name = editForm.name;
      if (editForm.model) updateData.model = editForm.model;
      if (editForm.number && editForm.number > 0) updateData.number = editForm.number;
      if (editForm.imei) {
        if (editForm.imei.length !== 15) {
          alert('IMEI должен содержать 15 цифр');
          return;
        }
        updateData.imei = editForm.imei;
      }
      if (editForm.airport !== undefined) updateData.airport = editForm.airport;
      if (editForm.isActive !== undefined) updateData.isActive = editForm.isActive;

      await carService.updateCar(editingCar.id, updateData);
      setEditingCar(null);
      setEditForm({
        name: '',
        model: '',
        number: 0,
        imei: '',
        airport: '',
        isActive: true
      });
      fetchCars();
      fetchCarStats();
      alert('Машина успешно обновлена!');
    } catch (err) {
      console.error('Error updating car:', err);
      alert('Ошибка при обновлении машины');
    }
  };

  const handleDeleteCar = async (car: Car) => {
    if (!window.confirm(`Вы уверены, что хотите удалить машину "${car.name}"?`)) {
      return;
    }

    try {
      await carService.deleteCar(car.id);
      fetchCars();
      fetchCarStats();
      alert('Машина успешно удалена!');
    } catch (err) {
      console.error('Error deleting car:', err);
      alert('Ошибка при удалении машины');
    }
  };

  const handleCreateDefaultCars = async () => {
    try {
      const defaultCars = carService.getDefaultCars();

      for (const carData of defaultCars) {
        await carService.createCar(carData);
      }

      fetchCars();
      fetchCarStats();
      alert('Стандартные машины успешно созданы!');
    } catch (err) {
      console.error('Error creating default cars:', err);
      alert('Ошибка при создании стандартных машин');
    }
  };

  const handleToggleCarActive = async (car: Car) => {
    try {
      await carService.toggleCarActive(car.id);
      fetchCars();
      fetchCarStats();
      alert(`Машина ${car.isActive ? 'деактивирована' : 'активирована'}!`);
    } catch (err) {
      console.error('Error toggling car status:', err);
      alert('Ошибка при изменении статуса машины');
    }
  };

  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectedCars.length === 0) {
      alert('Выберите машины для выполнения действия');
      return;
    }

    try {
      for (const carId of selectedCars) {
        switch (action) {
          case 'activate':
            await carService.toggleCarActive(carId);
            break;
          case 'deactivate':
            await carService.toggleCarActive(carId);
            break;
          case 'delete':
            await carService.deleteCar(carId);
            break;
        }
      }
      setSelectedCars([]);
      fetchCars();
      fetchCarStats();
      alert(`Действие выполнено для ${selectedCars.length} машин`);
    } catch (err) {
      console.error('Error performing bulk action:', err);
      alert('Ошибка при выполнении действия');
    }
  };

  const handleSelectCar = (carId: number) => {
    setSelectedCars(prev => 
      prev.includes(carId) 
        ? prev.filter(id => id !== carId)
        : [...prev, carId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCars.length === filteredCars.length) {
      setSelectedCars([]);
    } else {
      setSelectedCars(filteredCars.map(car => car.id));
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
  };

  const getAirportText = (airport: string | undefined) => {
    if (!airport) return '-';
    const airportMap: Record<string, string> = {
      'ALA': 'Алматы',
      'NQZ': 'Нур-Султан',
      'GUW': 'Атырау',
      'URA': 'Уральск',
      'AKX': 'Актобе',
      'SCO': 'Актау',
      'KGF': 'Караганда',
      'ASTANA': 'Астана',
      'ALMATY': 'Алматы'
    };
    return airportMap[airport] || airport;
  };

  const filteredCars = cars.filter(car => {
    const matchesSearch = 
      car.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.imei.includes(searchTerm) ||
      car.number.toString().includes(searchTerm);
    const matchesActive = !showActiveOnly || car.isActive;
    const matchesAirport = !selectedAirport || car.airport === selectedAirport;
    const matchesModel = !selectedModel || car.model === selectedModel;
    return matchesSearch && matchesActive && matchesAirport && matchesModel;
  });

  const openEditModal = (car: Car) => {
    setEditingCar(car);
    setEditForm({
      name: car.name,
      model: car.model,
      number: car.number,
      imei: car.imei,
      airport: car.airport || '',
      isActive: car.isActive
    });
  };

  const closeEditModal = () => {
    setEditingCar(null);
    setEditForm({
      name: '',
      model: '',
      number: 0,
      imei: '',
      airport: '',
      isActive: true
    });
  };

  if (loading) {
    return (
      <div className="car-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка машин...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="car-management">
      <div className="car-header">
        <div className="header-left">
          <h2>Управление машинами</h2>
          {carStats && (
            <div className="quick-stats">
              <span className="stat-item">
                <strong>{totalElements}</strong> всего
              </span>
              <span className="stat-item">
                <strong>{carStats.activeCars}</strong> активных
              </span>
              <span className="stat-item">
                <strong>{carStats.inactiveCars}</strong> неактивных
              </span>
            </div>
          )}
        </div>
        <div className="car-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="create-car-btn"
          >
            Создать машину
          </button>
          <div className="bulk-actions">
            <button onClick={() => handleBulkAction('delete')} className="bulk-delete-btn">
              Удалить выбранных
            </button>
          </div>
          <button 
            onClick={handleCreateDefaultCars} 
            className="create-default-btn"
          >
            Стандартные машины
          </button>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchCars} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="car-filters">
        <div className="search-section">
          <input
            type="text"
            placeholder="Поиск по названию, модели, номеру или IMEI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-section">
          <select
            value={selectedAirport}
            onChange={(e) => setSelectedAirport(e.target.value)}
            className="airport-filter"
          >
            <option value="">Все аэропорты</option>
            {airports.map(airport => (
              <option key={airport} value={airport}>{getAirportText(airport)}</option>
            ))}
          </select>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="model-filter"
          >
            <option value="">Все модели</option>
            {models.map(model => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
            />
            Только активные
          </label>
        </div>
        <div className="view-controls">
          <button onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')} className="view-toggle-btn">
            {viewMode === 'cards' ? 'Список' : 'Карточки'}
          </button>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="page-size-select"
          >
            <option value={5}>5 на странице</option>
            <option value={10}>10 на странице</option>
            <option value={20}>20 на странице</option>
            <option value={50}>50 на странице</option>
          </select>
        </div>
      </div>

      <div className="cars-list">
        {viewMode === 'cards' ? (
          filteredCars.map((car) => (
            <div key={car.id} className={`car-card ${!car.isActive ? 'inactive' : ''}`}>
              <div className="car-info">
                <div className="car-header-info">
                  <div className="car-number">№{car.number}</div>
                  <h3 className="car-name">{car.name}</h3>
                  <span className={`car-active-badge ${car.isActive ? 'active' : 'inactive'}`}>
                    {car.isActive ? 'Активна' : 'Неактивна'}
                  </span>
                </div>
                <div className="car-details">
                  <p><strong>Модель:</strong> {car.model}</p>
                  <p><strong>IMEI:</strong> {car.imei}</p>
                  {car.airport && <p><strong>Аэропорт:</strong> {getAirportText(car.airport)}</p>}
                  {car.calibrationTablesCount !== undefined && (
                    <p><strong>Тарировочных таблиц:</strong> {car.calibrationTablesCount}</p>
                  )}
                </div>
                <div className="car-meta">
                  <span>Создана: {new Date(car.createdAt).toLocaleDateString('ru-RU')}</span>
                  {car.updatedAt && <span>Обновлена: {new Date(car.updatedAt).toLocaleDateString('ru-RU')}</span>}
                </div>
              </div>
              <div className="car-actions">
                <button 
                  onClick={() => openEditModal(car)} 
                  className="edit-btn"
                >
                  Редактировать
                </button>
                <button 
                  onClick={() => handleDeleteCar(car)} 
                  className="delete-btn"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        ) : (
          <table className="car-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedCars.length === filteredCars.length && filteredCars.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('id')} className="sortable">
                  ID {sortBy === 'id' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('number')} className="sortable">
                  Номер {sortBy === 'number' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Название {sortBy === 'name' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('model')} className="sortable">
                  Модель {sortBy === 'model' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('imei')} className="sortable">
                  IMEI {sortBy === 'imei' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('airport')} className="sortable">
                  Аэропорт {sortBy === 'airport' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th>Статус</th>
                <th>Тарировочных таблиц</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredCars.map((car) => (
                <tr key={car.id} className={`car-row ${!car.isActive ? 'inactive' : ''}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedCars.includes(car.id)}
                      onChange={() => handleSelectCar(car.id)}
                    />
                  </td>
                  <td>{car.id}</td>
                  <td>№{car.number}</td>
                  <td>{car.name}</td>
                  <td>{car.model}</td>
                  <td>{car.imei}</td>
                  <td>{getAirportText(car.airport)}</td>
                  <td>
                    <span className={`car-active-badge ${car.isActive ? 'active' : 'inactive'}`}>
                      {car.isActive ? 'Активна' : 'Неактивна'}
                    </span>
                  </td>
                  <td>{car.calibrationTablesCount || 0}</td>
                  <td>
                    <button 
                      onClick={() => openEditModal(car)} 
                      className="edit-btn"
                    >
                      Редактировать
                    </button>
                    <button 
                      onClick={() => handleDeleteCar(car)} 
                      className="delete-btn"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredCars.length === 0 && !loading && (
        <div className="empty-state">
          <p>Машины не найдены</p>
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}>Назад</button>
          <span>Страница {currentPage + 1} из {totalPages}</span>
          <button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1}>Вперед</button>
        </div>
      )}

      {/* Статистика */}
      {carStats && (
        <div className="car-stats">
          <h3>Статистика машин</h3>
          <p><strong>Всего машин:</strong> {totalElements}</p>
          <p><strong>Активных машин:</strong> {carStats.activeCars}</p>
          <p><strong>Неактивных машин:</strong> {carStats.inactiveCars}</p>
          {carStats.carsByAirport && (
            <div className="airport-stats">
              <h4>Машины по аэропортам:</h4>
              {Object.entries(carStats.carsByAirport).map(([airport, count]) => (
                <p key={airport}><strong>{airport}:</strong> {String(count)}</p>
              ))}
            </div>
          )}
          {carStats.carsByModel && (
            <div className="model-stats">
              <h4>Машины по моделям:</h4>
              {Object.entries(carStats.carsByModel).map(([model, count]) => (
                <p key={model}><strong>{model}:</strong> {String(count)}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Создать новую машину</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Название *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Топливозаправщик №1"
                  />
                </div>
                <div className="form-group">
                  <label>Номер *</label>
                  <input
                    type="number"
                    value={createForm.number || ''}
                    onChange={(e) => setCreateForm({ ...createForm, number: parseInt(e.target.value) || 0 })}
                    placeholder="101"
                    min="1"
                    max="9999"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Модель *</label>
                <input
                  type="text"
                  value={createForm.model}
                  onChange={(e) => setCreateForm({ ...createForm, model: e.target.value })}
                  placeholder="КАМАЗ-53215"
                />
              </div>
              <div className="form-group">
                <label>IMEI *</label>
                <input
                  type="text"
                  value={createForm.imei}
                  onChange={(e) => setCreateForm({ ...createForm, imei: e.target.value })}
                  placeholder="123456789012345"
                  maxLength={15}
                />
                <small>15 цифр</small>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Аэропорт</label>
                  <select
                    value={createForm.airport}
                    onChange={(e) => setCreateForm({ ...createForm, airport: e.target.value })}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{airport}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={createForm.isActive}
                      onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.checked })}
                    />
                    Активна
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setIsCreateModalOpen(false)} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleCreateCar} className="save-btn">
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {editingCar && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Редактировать машину</h3>
              <button onClick={closeEditModal} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Название</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Топливозаправщик №1"
                  />
                </div>
                <div className="form-group">
                  <label>Номер</label>
                  <input
                    type="number"
                    value={editForm.number || ''}
                    onChange={(e) => setEditForm({ ...editForm, number: parseInt(e.target.value) || 0 })}
                    placeholder="101"
                    min="1"
                    max="9999"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Модель</label>
                <input
                  type="text"
                  value={editForm.model}
                  onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                  placeholder="КАМАЗ-53215"
                />
              </div>
              <div className="form-group">
                <label>IMEI</label>
                <input
                  type="text"
                  value={editForm.imei}
                  onChange={(e) => setEditForm({ ...editForm, imei: e.target.value })}
                  placeholder="123456789012345"
                  maxLength={15}
                />
                <small>15 цифр</small>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Аэропорт</label>
                  <select
                    value={editForm.airport}
                    onChange={(e) => setEditForm({ ...editForm, airport: e.target.value })}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{airport}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    />
                    Активна
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeEditModal} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleUpdateCar} className="save-btn">
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarManagement; 